import { useState, useEffect } from 'react';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Lock, Check, Loader2, ShieldCheck } from 'lucide-react';
import { api, formatApiError } from '@/admin/api';

const PaymentPage = () => {
  const [paymentType, setPaymentType] = useState('summit_registration');
  const [submitting, setSubmitting] = useState(false);
  const [testMode, setTestMode] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    amount: '2000',
    anonymous: false,
  });

  useEffect(() => {
    api.get('/api/payments/public-config')
      .then((r) => setTestMode(!!r.data.test_mode))
      .catch(() => {});
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const paymentOptions = [
    {
      id: 'summit_registration',
      title: 'Summit Registration',
      amount: 2000,
      display: '₦2,000',
      description: 'One-time 2026 Summit registration fee',
    },
    {
      id: 'donation',
      title: 'Donation',
      amount: null,
      display: 'Custom',
      description: 'Support our programs',
    },
  ];

  const selectType = (opt) => {
    setPaymentType(opt.id);
    if (opt.id === 'summit_registration') {
      setFormData((prev) => ({ ...prev, amount: String(opt.amount) }));
    } else {
      setFormData((prev) => ({ ...prev, amount: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const amt = parseInt(formData.amount, 10);
    if (!amt || amt < 100) {
      toast.error('Please enter a valid amount (minimum ₦100).');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post('/api/payments/initialize', {
        payment_type: paymentType,
        name: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        amount: amt,
        anonymous: paymentType === 'donation' ? formData.anonymous : false,
        callback_url: `${window.location.origin}/payment/success`,
      });
      // Full redirect to Paystack checkout (works on all mobile browsers)
      window.location.href = data.authorization_url;
    } catch (err) {
      toast.error(formatApiError(err));
      setSubmitting(false);
    }
  };

  const features = [
    'Payment handled securely by Paystack',
    'No card details stored on this site',
    'Instant confirmation after payment',
    'Verified by official webhook',
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <section className="relative bg-gradient-to-br from-primary/10 via-background to-accent/10 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-4 py-2 rounded-full mb-6">
              <Lock size={18} />
              <span className="text-sm font-medium">Secure Payment Portal</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold text-foreground mb-6">
              Payment & Contributions
            </h1>
            <p className="text-lg lg:text-xl text-muted-foreground leading-relaxed">
              Make secure payments for summit registration and donations.
            </p>
            {testMode && (
              <div className="mt-6 inline-flex items-center gap-2 text-xs bg-amber-100 text-amber-800 px-3 py-1.5 rounded-full" data-testid="test-mode-banner">
                <ShieldCheck size={14} /> Test mode — no real cards will be charged
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="py-20 bg-background">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-1 space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="font-serif">Payment Type</CardTitle>
                  <CardDescription>Select what you&apos;re paying for</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {paymentOptions.map((option) => (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => selectType(option)}
                      data-testid={`payment-type-${option.id}`}
                      className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                        paymentType === option.id
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-primary/50'
                      }`}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-semibold text-foreground">{option.title}</p>
                          <p className="text-sm text-muted-foreground mt-1">{option.description}</p>
                        </div>
                        <p className="text-primary font-bold">{option.display}</p>
                      </div>
                    </button>
                  ))}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="font-serif text-sm">What makes this safe</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {features.map((feature, index) => (
                    <div key={index} className="flex items-start space-x-2">
                      <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="text-primary" size={12} />
                      </div>
                      <p className="text-sm text-muted-foreground">{feature}</p>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>

            <div className="lg:col-span-2">
              <Card className="shadow-elevated">
                <CardHeader>
                  <CardTitle className="font-serif text-2xl">Payment Details</CardTitle>
                  <CardDescription>
                    After submitting, you&apos;ll be redirected to Paystack to complete payment.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-6" data-testid="payment-form">
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold text-foreground">Your Information</h3>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="fullName">Full Name *</Label>
                          <Input
                            id="fullName"
                            name="fullName"
                            value={formData.fullName}
                            onChange={handleInputChange}
                            required
                            placeholder="Enter your full name"
                            data-testid="pay-name"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="email">Email Address *</Label>
                          <Input
                            id="email"
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            required
                            placeholder="your.email@example.com"
                            data-testid="pay-email"
                          />
                        </div>
                      </div>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="phone">Phone Number *</Label>
                          <Input
                            id="phone"
                            name="phone"
                            type="tel"
                            value={formData.phone}
                            onChange={handleInputChange}
                            required
                            placeholder="+234 XXX XXX XXXX"
                            data-testid="pay-phone"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="amount">Amount (₦) *</Label>
                          <Input
                            id="amount"
                            name="amount"
                            type="number"
                            min="100"
                            value={formData.amount}
                            onChange={handleInputChange}
                            required
                            placeholder="Enter amount"
                            disabled={paymentType === 'summit_registration'}
                            data-testid="pay-amount"
                          />
                          {paymentType === 'summit_registration' && (
                            <p className="text-xs text-muted-foreground">
                              Fixed registration fee set by organisers.
                            </p>
                          )}
                        </div>
                      </div>
                      {paymentType === 'donation' && (
                        <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.anonymous}
                            onChange={(e) => setFormData((p) => ({ ...p, anonymous: e.target.checked }))}
                            className="w-4 h-4"
                            data-testid="pay-anonymous"
                          />
                          Donate anonymously (your name won&apos;t be published)
                        </label>
                      )}
                    </div>

                    <Button
                      type="submit"
                      disabled={submitting}
                      data-testid="pay-submit"
                      className="w-full bg-primary hover:bg-primary-dark text-white py-6 text-lg"
                    >
                      {submitting ? (
                        <Loader2 className="mr-2 animate-spin" size={20} />
                      ) : (
                        <Lock className="mr-2" size={20} />
                      )}
                      Complete Secure Payment
                    </Button>

                    <p className="text-xs text-center text-muted-foreground">
                      Payments are processed by Paystack. We never see or store your card details.
                    </p>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PaymentPage;
